import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { PhotoInfoType } from '@dreambooks/core';
import { ApiService } from './api.service';
import { UploadService } from './upload.service';
import { PathService } from './path.service';
import { GlobalsService } from './globals.service';
import { UserStateService } from './user-state.service';
import { findUnresolvedPlacedPhotos, collectPlacedPhotoStorageTargets, PlacedPhotoStorageTarget } from '../utils/placed-photo-validation';
import { fillMissingPhotoSizeDimensions } from '../utils/photo-size-dimensions';

/**
 * Prepared photo data ready for insertion into project.
 * Generated locally before any server upload.
 */
export interface PreparedPhoto {
  file: File;
  md5: string;
  name: string;
  width: number;
  height: number;
  thumbnailDataUrl: string;
}

/**
 * Result of checking project photos against the server.
 */
export interface RecoveryResult {
  /** MD5s of photos that exist on server — can be recovered with full data */
  recovered: string[];
  /** MD5s of photos truly missing from server — need re-upload */
  missing: string[];
}

/**
 * Full photo data returned by the server for recovered photos.
 */
export interface RecoveredPhoto {
  id: number;
  serverName: string;
  md5: string;
  width: number;
  height: number;
  serverId: number;
  pathStructureId: number;
  sizes: Array<{ name: string; width: number; height: number; url?: string }>;
}

/**
 * Result of cart validation — checks if all project photos are ready.
 */
export interface MissingPhotoDetail {
  md5: string;
  name: string;
  thumbnail?: string;
  projectId: number;
}

export interface CartValidationResult {
  valid: boolean;
  reason?: 'uploads_in_progress' | 'reuploads_in_progress' | 'photos_missing' | 'validation_failed';
  missingPhotos?: MissingPhotoDetail[];
  totalMissing?: number;
}

/**
 * UploadResilienceService — manages thumbnail-first project saves and
 * upload recovery for the album editor.
 *
 * Flow:
 * 1. User selects photos → preparePhotoBatch() generates thumbnails + MD5s
 * 2. Thumbnails saved as data URLs in project JSON (one save call)
 * 3. Full-res uploads run in background
 * 4. On project reload: checkMissingPhotos() detects incomplete uploads
 * 5. Recovered photos get server URLs; missing photos trigger re-upload dialog
 */
@Injectable({
  providedIn: 'root'
})
export class UploadResilienceService {
  private api = inject(ApiService);
  private http = inject(HttpClient);
  private uploadService = inject(UploadService);
  private pathService = inject(PathService);
  private globals = inject(GlobalsService);
  private userState = inject(UserStateService);

  // ─── Upload batch tracking ───

  private readonly _isUploading = signal(false);
  private readonly _totalCount = signal(0);
  private readonly _completedCount = signal(0);

  readonly isUploading = this._isUploading.asReadonly();
  readonly totalCount = this._totalCount.asReadonly();
  readonly completedCount = this._completedCount.asReadonly();
  readonly uploadProgress = computed(() => {
    const total = this._totalCount();
    if (total === 0) return 0;
    return Math.round((this._completedCount() / total) * 100);
  });

  // ─── beforeunload ───

  private beforeUnloadHandler: ((e: BeforeUnloadEvent) => void) | null = null;

  // ─── Persistent upload queue state ───
  // Held on the class (not local to queueBackgroundUploads) so repeated
  // calls to queueBackgroundUploads APPEND to the running queue instead of
  // resetting it. Lets the editor enqueue photos one at a time as they
  // finish local prep, so server uploads can run in parallel with the
  // remaining files' MD5 / thumbnail decode — instead of waiting for the
  // entire batch's prep to complete.
  private queue: { file: File; md5: string; objectUrl: string }[] = [];
  private active = 0;
  private static readonly UPLOAD_CONCURRENCY = 3;
  private queueProjectId = 0;

  // ─── Photo preparation (Step 1) ───

  /**
   * Prepare a batch of files for insertion into the project.
   * Generates thumbnail data URLs and computes MD5 hashes.
   * Processes in batches of 10 to avoid memory spikes.
   */
  async preparePhotoBatch(files: File[]): Promise<PreparedPhoto[]> {
    const results: PreparedPhoto[] = [];
    const BATCH_SIZE = 10;

    for (let i = 0; i < files.length; i += BATCH_SIZE) {
      const batch = files.slice(i, i + BATCH_SIZE);
      const batchResults = await Promise.all(
        batch.map(file => this.preparePhoto(file))
      );
      results.push(...batchResults.filter((r): r is PreparedPhoto => r !== null));
    }

    return results;
  }

  private async preparePhoto(file: File): Promise<PreparedPhoto | null> {
    try {
      // One decode produces BOTH the thumbnail AND the natural dimensions;
      // MD5 runs in parallel. See upload.service.ts → preparePhotoLocal.
      const [md5, prep] = await Promise.all([
        this.uploadService.computeMD5(file),
        this.uploadService.preparePhotoLocal(file)
      ]);

      if (!prep) return null;

      return {
        file,
        md5,
        name: file.name,
        width: prep.width,
        height: prep.height,
        thumbnailDataUrl: prep.thumbnailDataUrl
      };
    } catch (err) {
      console.error('[UploadResilience] Failed to prepare photo:', file.name, err);
      return null;
    }
  }

  // (getImageDimensions helper removed — dimensions are now returned by
  // uploadService.preparePhotoLocal as part of the single-decode prep.)

  // ─── Recovery (Step 2) ───

  /**
   * Check which photos from the project JSON actually exist on the server.
   * Called on project load when pending photos are detected.
   *
   * @param projectId The project's server ID
   * @param pendingMd5s MD5 hashes of photos that appear pending (no serverName/url)
   * @returns Which photos were recovered vs truly missing
   */
  async checkMissingPhotos(projectId: number, pendingMd5s: string[]): Promise<RecoveryResult> {
    if (pendingMd5s.length === 0) {
      return { recovered: [], missing: [] };
    }

    try {
      const response = await firstValueFrom(
        this.api.post<{ existing?: string[]; missing?: string[] }>(
          `/projects/${projectId}/photos/check`,
          { photos: pendingMd5s }
        )
      );

      return {
        recovered: response?.existing || [],
        missing: response?.missing || []
      };
    } catch (err) {
      console.error('[UploadResilience] Failed to check missing photos:', err);
      // On error, treat all as missing (safe fallback)
      return { recovered: [], missing: pendingMd5s };
    }
  }

  /**
   * Like checkMissingPhotos but FAIL-OPEN: a failed request returns null
   * instead of "everything missing". Use when verifying photos that already
   * carry a server identity — an endpoint hiccup must not mass-flag a whole
   * project as missing (and strip its render sources).
   */
  async checkPhotosExistence(projectId: number, md5s: string[]): Promise<RecoveryResult | null> {
    if (md5s.length === 0) return { recovered: [], missing: [] };
    try {
      const response = await firstValueFrom(
        this.api.post<{ existing?: string[]; missing?: string[] }>(
          `/projects/${projectId}/photos/check`,
          { photos: md5s }
        )
      );
      return {
        recovered: response?.existing || [],
        missing: response?.missing || []
      };
    } catch (err) {
      console.error('[UploadResilience] photos/check failed (fail-open):', err);
      return null;
    }
  }

  /**
   * Filter a project's photos down to the ones the server-side
   * /photos/check endpoint can meaningfully verify. Theme assets and derived
   * entries (backgrounds / cliparts / composition placeholders / demo photos)
   * live under different storage prefixes, so the endpoint would falsely
   * report them as missing.
   */
  private static readonly THEME_ASSET_TYPES = new Set<PhotoInfoType>([
    PhotoInfoType.Background,
    PhotoInfoType.Clipart,
    PhotoInfoType.ThemeBackground,
    PhotoInfoType.ThemeClipart,
  ]);

  filterCheckablePhotos(photos: any[]): any[] {
    return (photos || []).filter((p: any) =>
      p?.md5 &&
      !p.md5.startsWith('bg_') &&
      !p.md5.startsWith('clipart_') &&
      !p.md5.startsWith('comp_placeholder_') &&
      !UploadResilienceService.THEME_ASSET_TYPES.has(Number(p.type)) &&
      Number(p.type) !== PhotoInfoType.PhotoDemo
    );
  }

  /**
   * Get full photo data from the server for recovered photos.
   * Uses POST /api/projects/{id}/photos which returns full PhotoInfo with sizes.
   *
   * @param projectId The project's server ID
   * @param md5s MD5 hashes of photos to sync
   * @returns Array of recovered photo data from the server
   */
  async syncProjectPhotos(projectId: number, md5s: string[]): Promise<RecoveredPhoto[]> {
    if (md5s.length === 0) return [];

    try {
      const response = await firstValueFrom(
        this.api.post<{ photos?: any[] }>(
          `/projects/${projectId}/photos`,
          { photos: md5s }
        )
      );

      // Endpoint bodies flip between camelCase and snake_case depending on
      // the backend path — normalize so consumers can rely on RecoveredPhoto
      // (a snake_case body here made serverName read as undefined and a
      // confirmed re-upload get reported as an error).
      const normalized: RecoveredPhoto[] = (response?.photos || []).map((p: any) => ({
        id: p.id || p.photo_id || 0,
        serverName: p.serverName || p.server_name || '',
        md5: p.md5 || '',
        width: p.width || 0,
        height: p.height || 0,
        serverId: p.serverId || p.server_id || 0,
        pathStructureId: p.pathStructureId || p.path_structure_id || 0,
        sizes: (p.sizes || []).map((s: any) => ({
          name: s.name || s.serverName || s.server_name || '',
          width: s.width || 0,
          height: s.height || 0,
          url: s.url || undefined
        }))
      }));
      // Dimension-less size entries break PhotoUtil.getNearestSize (it ranks
      // by area and degenerates to the first entry — usually _t). Estimate
      // missing dims from the variant suffix before consumers store these.
      for (const photo of normalized) {
        fillMissingPhotoSizeDimensions(photo.sizes, photo.width, photo.height);
      }
      console.log('[UploadResilience] syncProjectPhotos', projectId, md5s.length, 'md5s — raw:', response, '→ normalized:', normalized);
      return normalized;
    } catch (err) {
      console.error('[UploadResilience] Failed to sync project photos:', err);
      return [];
    }
  }

  // ─── Global upload queue (survives component destruction) ───

  private readonly _currentFileName = signal('');
  readonly currentFileName = this._currentFileName.asReadonly();

  /** Optional callbacks — set by editor, cleared when editor is destroyed */
  private onUploadCompleteCallback: ((task: { file: File; md5: string; objectUrl: string }, result: any) => void) | null = null;
  private onUploadFailedCallback: ((task: { file: File; md5: string; objectUrl: string }) => void) | null = null;

  /**
   * Queue background uploads that continue even if the editor is destroyed.
   * Runs with concurrency 3. Updates global signals for topbar indicator.
   *
   * @param tasks Upload tasks from the editor
   * @param projectId Project ID for the upload endpoint
   * @param onComplete Optional callback for each completed upload (only works while editor is alive)
   */
  queueBackgroundUploads(
    tasks: { file: File; md5: string; objectUrl: string }[],
    projectId: number,
    onComplete?: (task: { file: File; md5: string; objectUrl: string }, result: any) => void,
    onFailed?: (task: { file: File; md5: string; objectUrl: string }) => void
  ): void {
    if (tasks.length === 0) return;

    // Refresh callbacks + project context. Callers passing `undefined`
    // intentionally KEEP existing callbacks (so per-photo appends from
    // the editor don't need to re-pass them every call).
    if (onComplete !== undefined) this.onUploadCompleteCallback = onComplete;
    if (onFailed !== undefined) this.onUploadFailedCallback = onFailed;
    if (projectId > 0) this.queueProjectId = projectId;

    // Append to the running queue (or start a fresh one).
    this.queue.push(...tasks);
    this._totalCount.update(n => n + tasks.length);

    if (!this._isUploading()) {
      this._isUploading.set(true);
      this._completedCount.set(0);
      this.setupBeforeUnload();
    }

    this.processQueueNext();
  }

  /** Internal queue pump. Idempotent — safe to call repeatedly. */
  private processQueueNext(): void {
    while (this.active < UploadResilienceService.UPLOAD_CONCURRENCY && this.queue.length > 0) {
      const task = this.queue.shift()!;
      this.active++;
      this._currentFileName.set(task.file.name);

      this.executeUpload(task, this.queueProjectId).finally(() => {
        this.active--;
        this._completedCount.update(n => n + 1);

        if (this.queue.length > 0) {
          this.processQueueNext();
        } else if (this.active === 0) {
          // All done
          this._isUploading.set(false);
          this._totalCount.set(0);
          this._completedCount.set(0);
          this._currentFileName.set('');
          this.onUploadCompleteCallback = null;
          this.onUploadFailedCallback = null;
          this.queueProjectId = 0;
          this.teardownBeforeUnload();
        }
      });
    }
  }

  /**
   * Clear the editor callback (call from editor's ngOnDestroy).
   * Uploads continue but won't try to update the destroyed component.
   */
  clearEditorCallback(): void {
    this.onUploadCompleteCallback = null;
    this.onUploadFailedCallback = null;
  }

  private async executeUpload(
    task: { file: File; md5: string; objectUrl: string },
    projectId: number
  ): Promise<void> {
    try {
      const result = await this.uploadService.uploadPhoto(task.file, task.md5, {
        projectId,
        allSizes: true
      });

      if (result && this.onUploadCompleteCallback) {
        try {
          this.onUploadCompleteCallback(task, result);
        } catch {
          // Editor was destroyed between check and call — ignore
        }
      }
    } catch (err) {
      console.error('[UploadResilience] Background upload failed:', task.file.name, err);
      if (this.onUploadFailedCallback) {
        try {
          this.onUploadFailedCallback(task);
        } catch {
          // Editor was destroyed — ignore
        }
      }
    }
  }

  // Legacy methods kept for compatibility
  startUploadBatch(count: number): void {
    this._isUploading.set(true);
    this._totalCount.set(count);
    this._completedCount.set(0);
    this.setupBeforeUnload();
  }

  onUploadComplete(): void {
    this._completedCount.update(n => n + 1);
  }

  endUploadBatch(): void {
    this._isUploading.set(false);
    this._totalCount.set(0);
    this._completedCount.set(0);
    this._currentFileName.set('');
    this.teardownBeforeUnload();
  }

  // ─── Re-upload queue (persists across dialog open/close) ───

  /** Per-photo status: md5 → { status, progress } */
  private readonly _reuploadStates = signal<Map<string, { status: 'uploading' | 'resolved' | 'error'; progress: number }>>(new Map());
  readonly reuploadStates = this._reuploadStates.asReadonly();
  private readonly _isReuploadProcessing = signal(false);
  readonly isReuploadProcessing = this._isReuploadProcessing.asReadonly();
  private readonly _reuploadCurrentFile = signal('');
  readonly reuploadCurrentFile = this._reuploadCurrentFile.asReadonly();
  private readonly _reuploadTotalCount = signal(0);
  readonly reuploadTotalCount = this._reuploadTotalCount.asReadonly();
  readonly reuploadResolvedCount = computed(() => {
    const states = this._reuploadStates();
    let count = 0;
    states.forEach(s => { if (s.status === 'resolved') count++; });
    return count;
  });
  readonly reuploadProgress = computed(() => {
    const total = this._reuploadTotalCount();
    if (total === 0) return 0;
    return Math.round((this.reuploadResolvedCount() / total) * 100);
  });
  private reuploadOnResolved: ((md5: string) => void) | null = null;

  /**
   * Queue re-upload of missing photos. Runs in this global service so it
   * survives dialog close/reopen.
   */
  queueReupload(
    files: File[],
    projectId: number,
    missingPhotos: any[],
    onResolved: (md5: string) => void
  ): void {
    this.reuploadOnResolved = onResolved;
    this._isReuploadProcessing.set(true);
    this._reuploadTotalCount.set(missingPhotos.length);

    const missingMd5s = new Set(missingPhotos.map((p: any) => p.md5));

    this.processReuploadQueue(files, projectId, missingMd5s, missingPhotos).finally(() => {
      this._isReuploadProcessing.set(false);
      this._reuploadCurrentFile.set('');
    });
  }

  private async processReuploadQueue(
    files: File[],
    projectId: number,
    missingMd5s: Set<string>,
    missingPhotos: any[]
  ): Promise<void> {
    for (const file of files) {
      let currentMd5: string | null = null;
      try {
        const md5 = await this.uploadService.computeMD5(file);
        currentMd5 = md5;
        if (!missingMd5s.has(md5)) continue;
        missingMd5s.delete(md5);

        this._reuploadCurrentFile.set(file.name);
        this.updateReuploadState(md5, 'uploading', 0);

        const result = await this.uploadService.uploadPhoto(file, md5, {
          projectId,
          allSizes: true,
          forceUpload: true,
          onProgress: (progress: number) => {
            this.updateReuploadState(md5, 'uploading', progress);
          }
        });

        // A re-upload counts as resolved ONLY when storage actually has the
        // file — i.e. the result carries a real storage identity (a serverName,
        // or an http(s) URL). A null result / thrown error / data:-only result
        // means the upload did NOT succeed; marking it 'resolved' here is what
        // let the operator save a `data:`/`uploaded:false` placeholder that the
        // exporter cannot download. Mark such cases 'error' and keep the photo
        // in the missing set instead of falsely reporting success.
        console.log('[UploadResilience][reupload]', file.name, md5, '— uploadPhoto result:',
          result ? { id: result.id, serverName: result.serverName, url: result.url, sizes: result.sizes?.length } : result);

        const httpUrl = typeof result?.url === 'string' && /^https?:\/\//i.test(result.url) ? result.url : '';
        const serverName = result?.serverName || '';
        if (!result || (!serverName && !httpUrl)) {
          // The bytes may have landed even though the confirmation step
          // failed (post-upload info fetch raced the server's size
          // generation, or a 502 under load). Ask the server before
          // declaring failure — resolveFromServer only reports success
          // when a real storage identity comes back.
          if (await this.resolveFromServer(projectId, md5, missingPhotos)) {
            this.updateReuploadState(md5, 'resolved', 100);
            this.reuploadOnResolved?.(md5);
          } else {
            this.updateReuploadState(md5, 'error', 0);
            missingMd5s.add(md5); // still missing / retryable
          }
          continue;
        }

        const photo = missingPhotos.find((p: any) => p.md5 === md5);
        if (photo) {
          if (result.id) photo.id = result.id;
          if (result.serverId) photo.serverId = result.serverId;
          if (result.pathStructureId) photo.pathStructureId = result.pathStructureId;
          photo.serverName = serverName || photo.serverName;
          // Never keep the `data:` preview: use the http URL the upload returned,
          // otherwise rebuild it from serverName via PathService (same as the
          // load-time recovery path in editor.component.ts).
          photo.url = httpUrl || this.pathService.getUrl({
            id: photo.id,
            serverId: photo.serverId,
            pathStructureId: photo.pathStructureId,
            serverName: photo.serverName
          } as any);
          photo.sizes = result.sizes || photo.sizes;
          fillMissingPhotoSizeDimensions(photo.sizes, photo.width, photo.height);
          photo.thumbnail = result.thumbnail || photo.thumbnail;
          this.markPhotoUploaded(photo);
        }

        this.updateReuploadState(md5, 'resolved', 100);
        this.reuploadOnResolved?.(md5);
      } catch (err) {
        console.error('[UploadResilience] Re-upload error:', file.name, err);
        if (currentMd5) {
          // A thrown finalize/502 does not necessarily mean storage is
          // missing the file — verify before surfacing the error. Never
          // mark a re-upload resolved without a confirmed storage identity.
          if (await this.resolveFromServer(projectId, currentMd5, missingPhotos)) {
            this.updateReuploadState(currentMd5, 'resolved', 100);
            this.reuploadOnResolved?.(currentMd5);
          } else {
            this.updateReuploadState(currentMd5, 'error', 0);
            missingMd5s.add(currentMd5);
          }
        }
      }
    }
  }

  /**
   * Last-chance verification after a failed re-upload confirmation: asks the
   * server whether the photo exists now and, if so, pulls its identity into
   * the missingPhotos entry (same healing the load-time recovery applies).
   * Returns true only when a real serverName was applied — the
   * anti-false-success gate stays intact.
   */
  private async resolveFromServer(projectId: number, md5: string, missingPhotos: any[]): Promise<boolean> {
    // /projects/{id}/photos[/check] are project-scoped — id 0 (direct-file
    // re-uploads from the products flow) is not a valid target; skip the
    // second-chance confirmation instead of deterministically 404ing.
    if (!projectId || projectId <= 0) return false;
    try {
      // The photo/project association and size generation can lag the upload
      // response — retry the existence check briefly before giving up.
      let confirmed = false;
      for (const delay of [0, 3000, 8000]) {
        if (delay > 0) await new Promise(resolve => setTimeout(resolve, delay));
        const check = await this.checkPhotosExistence(projectId, [md5]);
        console.log('[UploadResilience][resolveFromServer]', md5, 'check after', delay, 'ms:', check);
        if (check?.recovered.includes(md5)) {
          confirmed = true;
          break;
        }
      }
      if (!confirmed) {
        console.warn('[UploadResilience][resolveFromServer]', md5, 'NOT confirmed by /photos/check — keeping error state');
        return false;
      }

      const [serverPhoto] = await this.syncProjectPhotos(projectId, [md5]);
      console.log('[UploadResilience][resolveFromServer]', md5, 'serverPhoto:', serverPhoto);
      if (!serverPhoto?.serverName) {
        console.warn('[UploadResilience][resolveFromServer]', md5, 'sync returned no serverName — keeping error state');
        return false;
      }

      const photo = missingPhotos.find((p: any) => p.md5 === md5);
      if (photo) {
        const base = {
          id: serverPhoto.id,
          serverId: serverPhoto.serverId,
          pathStructureId: serverPhoto.pathStructureId,
          serverName: serverPhoto.serverName
        };
        photo.id = serverPhoto.id;
        photo.serverId = serverPhoto.serverId;
        photo.pathStructureId = serverPhoto.pathStructureId;
        photo.serverName = serverPhoto.serverName;
        photo.url = this.pathService.getUrl(base as any);
        photo.thumbnail = this.pathService.getThumbnailUrl(base as any) || photo.thumbnail;
        if (serverPhoto.sizes?.length) {
          photo.sizes = serverPhoto.sizes.map(s => ({
            name: s.name || '',
            width: s.width || 0,
            height: s.height || 0,
            url: s.url || (s.name ? this.pathService.getUrl({ ...base, serverName: s.name } as any) : '')
          }));
          fillMissingPhotoSizeDimensions(photo.sizes, photo.width, photo.height);
        }
        this.markPhotoUploaded(photo);
      }
      console.log('[UploadResilience] Re-upload confirmed on server after failed response:', md5);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * PhotoInfo class instances from @dreambooks/core expose `uploaded` as a
   * getter-only property (derived from id + url) — a plain assignment throws
   * TypeError and used to send a fully successful re-upload down the error
   * path. Plain JSON photo objects carry it as a data property that must be
   * kept in sync, so set it only when writable.
   */
  private markPhotoUploaded(photo: any): void {
    try {
      photo.uploaded = true;
    } catch {
      // Getter-only: already true via the id/url the caller just applied.
    }
  }

  private updateReuploadState(md5: string, status: 'uploading' | 'resolved' | 'error', progress: number): void {
    this._reuploadStates.update(map => {
      const next = new Map(map);
      next.set(md5, { status, progress });
      return next;
    });
  }

  clearReuploadState(): void {
    this._reuploadStates.set(new Map());
    this._isReuploadProcessing.set(false);
    this._reuploadCurrentFile.set('');
    this._reuploadTotalCount.set(0);
    this.reuploadOnResolved = null;
  }

  /**
   * Invalidate stale resolved/error re-upload states for the given md5s.
   * A FRESH server validation that reports these photos missing supersedes a
   * previous session round that marked them resolved (a re-upload restores
   * the storage file but cannot rewrite the saved project JSON, so the next
   * validation can legitimately re-flag the same photos). In-flight
   * 'uploading' entries are kept.
   */
  resetReuploadStatesFor(md5s: string[]): void {
    if (!md5s.length) return;
    this._reuploadStates.update(map => {
      let changed = false;
      const next = new Map(map);
      for (const md5 of md5s) {
        const state = next.get(md5);
        if (state && state.status !== 'uploading') {
          next.delete(md5);
          changed = true;
        }
      }
      return changed ? next : map;
    });
  }

  // ─── Cart validation ───

  /**
   * Validate that all project photos are ready before adding to cart.
   * Level 1: client-side check (uploads/re-uploads in progress)
   * Level 2: server-side check — loads each project JSON, extracts photo MD5s,
   *          then calls POST /api/projects/{id}/photos/check to verify server has them
   */
  async validateForCart(projectIds: number[]): Promise<CartValidationResult> {
    // Level 1: client checks (instant)
    if (this._isUploading()) {
      return { valid: false, reason: 'uploads_in_progress' };
    }
    if (this._isReuploadProcessing()) {
      return { valid: false, reason: 'reuploads_in_progress' };
    }

    // Level 2: server checks for each project
    const validIds = projectIds.filter(id => id > 0);
    if (validIds.length === 0) {
      return { valid: true };
    }

    const allMissing: MissingPhotoDetail[] = [];
    let probeUnverified = false;

    for (const projectId of validIds) {
      try {
        // Step 1: Get project metadata (uses ApiService for JWT auto-refresh)
        const metadata = await firstValueFrom(
          this.api.get<any>(`/projects/${projectId}`)
        );

        if (!metadata?.url) continue;

        // Step 2: Load project JSON to get photo list
        const sep = metadata.url.includes('?') ? '&' : '?';
        const projectJson = await firstValueFrom(
          this.http.get<any>(metadata.url + sep + 't=' + Date.now())
        );

        // Step 3: Block on the saved project shape before the advisory server check.
        const unresolvedPlaced = findUnresolvedPlacedPhotos(projectJson, this.pathService);
        if (unresolvedPlaced.length > 0) {
          const missingPhotos = unresolvedPlaced.map(photo => ({
            md5: photo.md5,
            name: photo.name,
            projectId
          }));
          return {
            valid: false,
            reason: 'photos_missing',
            missingPhotos,
            totalMissing: missingPhotos.length
          };
        }

        // Step 4: STORAGE existence probe. Identity can be intact while the
        // physical file is gone (storage loss — the DB may even hold the md5
        // mangled to `..._recovering`, awaiting re-upload). The shape check
        // above cannot see that, and the md5-based /photos/check below cannot
        // either: it is DB-only, and direct-file albums place their pages as
        // `bg_*` entries with SYNTHETIC md5s, so their check list comes out
        // EMPTY (order 6519356: six 404ing pages sailed through). Load the
        // smallest size of each placed photo straight from storage
        // (cache-busted <img> — the editor's own missing-file detection) and
        // collect the definitive 404s.
        const storageTargets = collectPlacedPhotoStorageTargets(projectJson, this.pathService);
        if (storageTargets.length > 0) {
          const probe = await this.probePlacedPhotoStorage(storageTargets);
          if (probe.missing.length > 0) {
            console.warn('[UploadResilience] Project', projectId, 'has', probe.missing.length, 'placed photo(s) missing from STORAGE');
            for (const target of probe.missing) {
              allMissing.push({ md5: target.md5, name: target.name, thumbnail: target.thumbnail, projectId });
            }
          }
          if (probe.unverified.length > 0) {
            console.warn('[UploadResilience] Project', projectId, ':', probe.unverified.length, 'storage probe(s) did not complete — blocking as unverified');
            probeUnverified = true;
          }
        }

        // Step 5: Extract photos from project for the secondary advisory check
        // (theme assets + derived entries excluded — see filterCheckablePhotos).
        const photos: any[] = projectJson?.photos || projectJson?.product?.photos || [];
        const validPhotos = this.filterCheckablePhotos(photos);
        const md5s = validPhotos.map((p: any) => p.md5);

        if (md5s.length === 0) continue;

        // Step 6: Check which photos exist on server
        const response = await firstValueFrom(
          this.api.post<{ existing?: string[]; missing?: string[] }>(
            `/projects/${projectId}/photos/check`,
            { photos: md5s }
          )
        );

        const missingMd5s = new Set(response?.missing || []);

        if (missingMd5s.size > 0) {
          // Collect photo details for missing ones
          for (const photo of validPhotos) {
            if (missingMd5s.has(photo.md5)) {
              const sizes = photo.sizes || [];
              const thumbSize = sizes.find((s: any) => s.name?.includes('_t')) || sizes[0];
              allMissing.push({
                md5: photo.md5,
                name: photo.name || photo.md5,
                thumbnail: photo.thumbnail || thumbSize?.url || undefined,
                projectId
              });
            }
          }
          console.warn('[UploadResilience] Project', projectId, 'has', missingMd5s.size, 'missing/inaccessible photos');
        }
      } catch (err) {
        // FAIL-CLOSED: an error while validating a project used to be
        // swallowed (the project was skipped entirely), which let broken
        // projects into the cart whenever the metadata/JSON fetch or the
        // server check hiccupped. Block and let the user retry instead.
        console.error('[UploadResilience] Failed to validate project', projectId, '— blocking (fail-closed):', err);
        return { valid: false, reason: 'validation_failed' };
      }
    }

    if (allMissing.length > 0) {
      return { valid: false, reason: 'photos_missing', missingPhotos: allMissing, totalMissing: allMissing.length };
    }

    if (probeUnverified) {
      return { valid: false, reason: 'validation_failed' };
    }

    return { valid: true };
  }

  // ─── Storage existence probe (cart gate) ───

  private static readonly STORAGE_PROBE_CONCURRENCY = 6;
  private static readonly STORAGE_PROBE_TIMEOUT_MS = 8000;
  private static readonly STORAGE_PROBE_BUDGET_MS = 25000;

  /**
   * Probe every placed photo's storage URL. `missing` = the load errored
   * twice (definitive 404 — one retry absorbs transient blips);
   * `unverified` = the probe could not complete (timeout / global budget),
   * which the caller blocks as validation_failed rather than letting through.
   */
  private async probePlacedPhotoStorage(
    targets: PlacedPhotoStorageTarget[]
  ): Promise<{ missing: PlacedPhotoStorageTarget[]; unverified: PlacedPhotoStorageTarget[] }> {
    const missing: PlacedPhotoStorageTarget[] = [];
    const unverified: PlacedPhotoStorageTarget[] = [];
    const firstFailures: PlacedPhotoStorageTarget[] = [];
    const deadline = Date.now() + UploadResilienceService.STORAGE_PROBE_BUDGET_MS;

    let index = 0;
    const worker = async (): Promise<void> => {
      while (index < targets.length) {
        const target = targets[index++];
        if (Date.now() > deadline) {
          unverified.push(target);
          continue;
        }
        const result = await this.probeImageUrl(target.probeUrl);
        if (result === 'missing') firstFailures.push(target);
        else if (result === 'unverified') unverified.push(target);
      }
    };
    const workers = Math.min(UploadResilienceService.STORAGE_PROBE_CONCURRENCY, targets.length);
    await Promise.all(Array.from({ length: workers }, () => worker()));

    // One retry for the load errors — a transient network blip must not
    // block a valid cart add behind a scary "photos missing" list.
    for (const target of firstFailures) {
      if (Date.now() > deadline) {
        unverified.push(target);
        continue;
      }
      const result = await this.probeImageUrl(target.probeUrl);
      if (result === 'missing') missing.push(target);
      else if (result === 'unverified') unverified.push(target);
    }

    return { missing, unverified };
  }

  /** Cache-busted <img> load — no CORS involved, and the URL is never persisted. */
  private probeImageUrl(url: string): Promise<'ok' | 'missing' | 'unverified'> {
    return new Promise(resolve => {
      const img = new Image();
      let settled = false;
      let timer: ReturnType<typeof setTimeout> | undefined;
      const finish = (result: 'ok' | 'missing' | 'unverified'): void => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        img.onload = null;
        img.onerror = null;
        resolve(result);
      };
      timer = setTimeout(() => finish('unverified'), UploadResilienceService.STORAGE_PROBE_TIMEOUT_MS);
      img.onload = () => finish('ok');
      img.onerror = () => finish('missing');
      const sep = url.includes('?') ? '&' : '?';
      img.src = `${url}${sep}t=${Date.now()}`;
    });
  }

  // ─── beforeunload ───

  private setupBeforeUnload(): void {
    if (this.beforeUnloadHandler) return;
    this.beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      if (this._isUploading()) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  private teardownBeforeUnload(): void {
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
  }
}
